import React, { useState, useContext, useCallback, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { withRouter } from 'react-router'
import {
    Box,
    Button,
    Alert,
    Divider,
    Grid,
    IconButton,
    Typography,
} from '@mui/material'
import { LoadingDefault } from '@/shared/ui/loading'
import TwoFactorAuthentication from './TwoFactorAuthentication'
import Api from '../../Api'
import { KeDoContext } from '../../kedo-context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash } from '@fortawesome/pro-duotone-svg-icons/faEyeSlash'
import { faLock } from '@fortawesome/pro-duotone-svg-icons/faLock'
import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser'
import { faEye } from '@fortawesome/pro-duotone-svg-icons/faEye'
import azureLoginImage from '../../static/ms-symbollockup_signin_light.svg'
import { LoginFormInput } from '@/shared/ui/input'
import { StyledButton } from '@/shared/ui/buttons'

function isMoreThan24HoursAgo(date: number): boolean {
    const previousDate = new Date(date)
    const currentDate = new Date()
    return (
        (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60) > 24
    )
}

interface LoginProps {
    handleSuccessfulAuth: (data: any) => void
    title?: string
}

interface KedoContextType {
    t: (key: string) => string
    api: () => {
        getGoogleConnect: () => string
        getAzureConnect: () => string
    }
}

const Login: React.FC<LoginProps> = ({ handleSuccessfulAuth, title }) => {
    const kedo = useContext<KedoContextType>(KeDoContext)
    const location = useLocation()

    const [loading, setLoading] = useState(false)
    const [loginErrors, setLoginErrors] = useState(false)
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showOtp, setShowOtp] = useState(false)

    useEffect(() => {
        if (
            location.state?.from?.pathname &&
            location.state.from.pathname !== '/login'
        ) {
            const lastVisit = localStorage.getItem('lastVisit')

            if (lastVisit && isMoreThan24HoursAgo(Number(lastVisit))) {
                localStorage.setItem('referer', '/')
            } else {
                localStorage.setItem('referer', location.state.from.pathname)
            }
        }
    }, [location])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        if (name === 'username') setUsername(value)
        if (name === 'password') setPassword(value)
        setLoginErrors(false)
    }

    const submitLogin = useCallback(() => {
        setLoading(true)
        const api = new Api()

        api.postRaw('/login_check', { username, password })
            .then((response: any) => {
                if (response.data) {
                    handleSuccessfulAuth(response.data)
                }
                setLoading(false)
            })
            .catch((error: any) => {
                if (
                    error.response &&
                    error.response.status === 401 &&
                    error.response.data.errors?.json === true
                ) {
                    setShowOtp(true)
                } else {
                    setLoginErrors(true)
                }
                setLoading(false)
            })
    }, [username, password, handleSuccessfulAuth])

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            submitLogin()
        },
        [submitLogin]
    )

    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev)
    }

    const connectAzure = () => {
        window.location.assign(kedo.api().getAzureConnect())
    }

    if (loading) {
        return <LoadingDefault />
    }

    if (showOtp) {
        return (
            <TwoFactorAuthentication
                kedo={kedo}
                username={username}
                password={password}
                onSuccess={handleSuccessfulAuth}
            />
        )
    }

    return (
        <Box component={'form'} onSubmit={handleSubmit}>
            {title && (
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: '500' }}
                >
                    {title}
                </Typography>
            )}
            <Divider></Divider>
            {loginErrors && (
                <Alert severity="error" sx={{ mb: 2 }} data-cy="login-error">
                    {kedo.t('Incorrect username or password')}
                </Alert>
            )}
            <Grid container>
                <Grid size={12} mb={3}>
                    <LoginFormInput
                        type="text"
                        name={'username'}
                        dataCy="login-username"
                        placeholder={kedo.t('Username')}
                        value={username}
                        handleChange={handleChange}
                        startIcon={<FontAwesomeIcon icon={faUser} />}
                    />
                </Grid>

                <Grid size={12}>
                    <LoginFormInput
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        dataCy="login-password"
                        placeholder={kedo.t('Password')}
                        value={password}
                        handleChange={handleChange}
                        startIcon={<FontAwesomeIcon icon={faLock} />}
                        endIcon={
                            <IconButton
                                onClick={handleTogglePassword}
                                data-cy="password-visibility-toggle"
                                aria-label={kedo.t(
                                    showPassword
                                        ? 'Hide password'
                                        : 'Show password'
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={showPassword ? faEyeSlash : faEye}
                                />
                            </IconButton>
                        }
                    />
                </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
                <Box
                    component={Link}
                    to={'/forgot_password'}
                    className="forgot-password"
                    data-cy="forgot-password-link"
                    sx={{
                        display: 'inline-flex',
                        width: 'fit-content',
                    }}
                >
                    <Typography
                        variant="body2"
                        component="small"
                        sx={{ mt: 0.75 }}
                    >
                        {kedo.t('Forgot password')}
                    </Typography>
                </Box>
            </Box>

            <StyledButton
                fullWidth
                color={'primary'}
                type="submit"
                data-cy="login-submit"
                disabled={!username || !password}
                title={
                    !username || !password
                        ? 'Enter a username and password to continue'
                        : undefined
                }
            >
                {kedo.t('Continue')}
            </StyledButton>
            <Box sx={{ m: 2 }}>
                <Divider>
                    <Typography variant="body2" sx={{ mx: 2 }}>
                        {kedo.t('OR')}
                    </Typography>
                </Divider>
            </Box>

            <Button
                variant={'text'}
                onClick={connectAzure}
                title={kedo.t('Login with Azure')}
                className="shadow-none button-focus"
                data-cy="azure-login"
                aria-label={kedo.t('Login with Azure')}
            >
                <img src={azureLoginImage} alt="Logo microsoft" />
            </Button>
        </Box>
    )
}

export default withRouter(Login)
